import { useCallback, useEffect, useRef, useState } from "react";

export interface RemoteState<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  refreshing: boolean;
  reload: () => void;
}

export function useRemote<T>(loader: () => Promise<T>, dependencies: readonly unknown[], pollMs?: number): RemoteState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [nonce, setNonce] = useState(0);
  const mounted = useRef(true);
  const requestId = useRef(0);
  const hasData = useRef(false);
  const backgroundReload = useRef(false);

  const load = useCallback(async (background = false) => {
    const currentRequestId = ++requestId.current;
    background ? setRefreshing(true) : setLoading(true);
    try {
      const next = await loader();
      if (!mounted.current || currentRequestId !== requestId.current) return;
      setData(next);
      hasData.current = true;
      setError(null);
    } catch (nextError) {
      if (!mounted.current || currentRequestId !== requestId.current) return;
      setError(nextError instanceof Error ? nextError : new Error("Error desconocido"));
    } finally {
      if (mounted.current && currentRequestId === requestId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  useEffect(() => {
    mounted.current = true;
    void load(backgroundReload.current && hasData.current);
    backgroundReload.current = false;
    return () => { mounted.current = false; };
  }, [load, nonce]);

  useEffect(() => {
    if (!pollMs) return;
    const timer = window.setInterval(() => void load(true), pollMs);
    return () => window.clearInterval(timer);
  }, [load, pollMs]);

  const reload = useCallback(() => {
    backgroundReload.current = true;
    setNonce((value) => value + 1);
  }, []);
  return { data, error, loading, refreshing, reload };
}
